import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  MeetingInput,
  MeetingPreferencesInput,
  SchedulingPageInput,
} from "@sales-ops-amplify/shared";
import {
  cancelMeeting,
  completeMeeting,
  createMeeting,
  createSchedulingPage,
  deleteSchedulingPage,
  getMeetingPreferences,
  listMeetings,
  listSchedulingPages,
  noShowMeeting,
  syncMeetings,
  updateMeeting,
  updateMeetingPreferences,
  updateSchedulingPage,
  type MeetingView,
} from "@/api/meetings";

export function useMeetings(params?: {
  view?: MeetingView;
  q?: string;
  leadId?: string;
  opportunityId?: string;
}) {
  return useQuery({
    queryKey: ["meetings", params],
    queryFn: () => listMeetings(params),
  });
}

export function useMeetingPreferences() {
  return useQuery({
    queryKey: ["meeting-preferences"],
    queryFn: getMeetingPreferences,
  });
}

export function useSchedulingPages() {
  return useQuery({
    queryKey: ["scheduling-pages"],
    queryFn: listSchedulingPages,
  });
}

export function useCreateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: MeetingInput) => createMeeting(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["meetings"] });
    },
  });
}

export function useMeetingStatusAction() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      meetingId: string;
      action: "cancel" | "complete" | "no_show";
      outcomeNotes?: string;
    }) => {
      if (args.action === "cancel")
        return cancelMeeting(args.meetingId, args.outcomeNotes);
      if (args.action === "complete")
        return completeMeeting(args.meetingId, args.outcomeNotes);
      return noShowMeeting(args.meetingId, args.outcomeNotes);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["meetings"] });
    },
  });
}

export function useUpdateMeeting() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      meetingId: string;
      patch: Parameters<typeof updateMeeting>[1];
    }) => updateMeeting(args.meetingId, args.patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["meetings"] });
    },
  });
}

export function useSyncMeetings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => syncMeetings(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["meetings"] });
    },
  });
}

export function useUpdateMeetingPreferences() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: MeetingPreferencesInput) =>
      updateMeetingPreferences(patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["meeting-preferences"] });
    },
  });
}

export function useCreateSchedulingPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SchedulingPageInput) => createSchedulingPage(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["scheduling-pages"] });
    },
  });
}

export function useUpdateSchedulingPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: {
      pageId: string;
      patch: Partial<SchedulingPageInput>;
    }) => updateSchedulingPage(args.pageId, args.patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["scheduling-pages"] });
    },
  });
}

export function useDeleteSchedulingPage() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (pageId: string) => deleteSchedulingPage(pageId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["scheduling-pages"] });
    },
  });
}
